import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { FileDown, FileSpreadsheet, Search } from "lucide-react";
import { openAuthDownload } from "../lib/download";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export function CollectionReport({ renderTable }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const [mode, setMode] = useState("single");
    const [from, setFrom] = useState(today());
    const [to, setTo] = useState(today());
    const [employee, setEmployee] = useState("");
    const [staff, setStaff] = useState([]);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (isAdmin) api.get("/users").then((r) => setStaff(r.data || [])).catch(() => {});
    }, [isAdmin]);

    function query(fmt) {
        const p = new URLSearchParams({ from, to: mode === "single" ? from : to, fmt });
        if (isAdmin && employee) p.set("employee", employee);
        return `/reports/collections?${p.toString()}`;
    }

    async function load() {
        setBusy(true);
        try {
            const r = await api.get(query("json"));
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    }

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-4" data-testid="collection-report">
            <Card className="rounded-2xl border-slate-100 p-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex rounded-xl border border-slate-200 overflow-hidden text-sm">
                        <button type="button" data-testid="colrep-mode-single"
                            className={`px-3 h-10 ${mode === "single" ? "bg-blue-900 text-white" : "bg-white text-slate-700"}`}
                            onClick={() => setMode("single")}>Single date</button>
                        <button type="button" data-testid="colrep-mode-range"
                            className={`px-3 h-10 ${mode === "range" ? "bg-blue-900 text-white" : "bg-white text-slate-700"}`}
                            onClick={() => setMode("range")}>Date range</button>
                    </div>
                    <div>
                        <Label className="text-xs">{mode === "single" ? "Date" : "From"}</Label>
                        <Input type="date" value={from} max={today()} onChange={(e) => setFrom(e.target.value)}
                            className="h-10 rounded-xl" data-testid="colrep-from" />
                    </div>
                    {mode === "range" && (
                        <div>
                            <Label className="text-xs">To</Label>
                            <Input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)}
                                className="h-10 rounded-xl" data-testid="colrep-to" />
                        </div>
                    )}
                    {isAdmin && (
                        <div>
                            <Label className="text-xs">Staff</Label>
                            <select value={employee} onChange={(e) => setEmployee(e.target.value)}
                                className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white min-w-[160px]"
                                data-testid="colrep-employee">
                                <option value="">All staff</option>
                                <option value={user.username}>{user.name || user.username} (me)</option>
                                {staff.filter((s) => s.username !== user.username).map((s) => (
                                    <option key={s.id} value={s.username}>{s.name || s.username}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <Button onClick={load} disabled={busy} className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                        data-testid="colrep-view">
                        <Search className="h-4 w-4 mr-1.5" /> {busy ? "Loading…" : "View"}
                    </Button>
                    <div className="flex gap-2 ml-auto">
                        <Button variant="outline" className="h-10 rounded-xl" data-testid="colrep-pdf"
                            onClick={() => openAuthDownload(query("pdf"))}>
                            <FileDown className="h-4 w-4 mr-1.5" /> PDF
                        </Button>
                        <Button variant="outline" className="h-10 rounded-xl" data-testid="colrep-excel"
                            onClick={() => openAuthDownload(query("excel"))}>
                            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
                        </Button>
                    </div>
                </div>
                {!isAdmin && (
                    <p className="text-xs text-slate-500 mt-3" data-testid="colrep-own-note">
                        Showing only your own approved collections.
                    </p>
                )}
            </Card>
            {data ? renderTable(data) : (
                <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">Loading…</Card>
            )}
        </div>
    );
}
